import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';

// Auth
import LoginPage    from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';

import ProfilePage from './pages/common/ProfilePage';
import LandingPage from './pages/common/LandingPage';

// Patient — import directly from source file
import PatientDashboard from './pages/patient/PatientDashboard';
import {
  PatientAppointments,
  PatientPrescriptions,
  PatientProgress,
  PatientNotifications,
} from './pages/patient/PatientSubPages';

// Doctor — import directly from source file
import DoctorDashboard from './pages/doctor/DoctorDashboard';
import {
  DoctorPatients,
  DoctorAttendance,
  DoctorAppointments,
  DoctorPrescriptions,
} from './pages/doctor/DoctorSubPages';

// Admin
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers     from './pages/admin/AdminUsers';
import AdminBeds      from './pages/admin/AdminBeds';
import {
  AdminAppointments,
  AdminAnalytics,
  AdminCenters,
} from './pages/admin/AdminSubPages';

const Spinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-emerald-50">
    <div className="flex flex-col items-center gap-3">
      <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      <p className="text-sm text-emerald-600 font-medium">Loading Avartana Care…</p>
    </div>
  </div>
);

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user)   return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role))
    return <Navigate to={'/' + user.role + '/dashboard'} replace />;
  return children;
};

const AppRoutes = () => {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;

  return (
    <Routes>
      {/* Root / Landing Page */}
      <Route
        path="/"
        element={user ? <Navigate to={'/' + user.role + '/dashboard'} replace /> : <LandingPage />}
      />

      {/* Public */}
      <Route path="/login"    element={user ? <Navigate to={'/' + user.role + '/dashboard'} replace /> : <LoginPage />} />
      <Route path="/register" element={user ? <Navigate to={'/' + user.role + '/dashboard'} replace /> : <RegisterPage />} />

      {/* ── Patient ── */}
      <Route path="/patient/dashboard"     element={<ProtectedRoute allowedRoles={['patient']}><PatientDashboard    /></ProtectedRoute>} />
      <Route path="/patient/appointments"  element={<ProtectedRoute allowedRoles={['patient']}><PatientAppointments /></ProtectedRoute>} />
      <Route path="/patient/prescriptions" element={<ProtectedRoute allowedRoles={['patient']}><PatientPrescriptions/></ProtectedRoute>} />
      <Route path="/patient/progress"      element={<ProtectedRoute allowedRoles={['patient']}><PatientProgress      /></ProtectedRoute>} />
      <Route path="/patient/notifications" element={<ProtectedRoute allowedRoles={['patient']}><PatientNotifications /></ProtectedRoute>} />
      <Route path="/patient/profile"       element={<ProtectedRoute allowedRoles={['patient']}><ProfilePage         /></ProtectedRoute>} />

      {/* ── Doctor ── */}
      <Route path="/doctor/dashboard"     element={<ProtectedRoute allowedRoles={['doctor']}><DoctorDashboard    /></ProtectedRoute>} />
      <Route path="/doctor/patients"      element={<ProtectedRoute allowedRoles={['doctor']}><DoctorPatients     /></ProtectedRoute>} />
      <Route path="/doctor/attendance"    element={<ProtectedRoute allowedRoles={['doctor']}><DoctorAttendance   /></ProtectedRoute>} />
      <Route path="/doctor/appointments"  element={<ProtectedRoute allowedRoles={['doctor']}><DoctorAppointments /></ProtectedRoute>} />
      <Route path="/doctor/prescriptions" element={<ProtectedRoute allowedRoles={['doctor']}><DoctorPrescriptions/></ProtectedRoute>} />
      <Route path="/doctor/profile"       element={<ProtectedRoute allowedRoles={['doctor']}><ProfilePage        /></ProtectedRoute>} />

      {/* ── Admin ── */}
      <Route path="/admin/dashboard"    element={<ProtectedRoute allowedRoles={['admin']}><AdminDashboard    /></ProtectedRoute>} />
      <Route path="/admin/users"        element={<ProtectedRoute allowedRoles={['admin']}><AdminUsers        /></ProtectedRoute>} />
      <Route path="/admin/appointments" element={<ProtectedRoute allowedRoles={['admin']}><AdminAppointments /></ProtectedRoute>} />
      <Route path="/admin/beds"         element={<ProtectedRoute allowedRoles={['admin']}><AdminBeds         /></ProtectedRoute>} />
      <Route path="/admin/analytics"    element={<ProtectedRoute allowedRoles={['admin']}><AdminAnalytics    /></ProtectedRoute>} />
      <Route path="/admin/centers"      element={<ProtectedRoute allowedRoles={['admin']}><AdminCenters      /></ProtectedRoute>} />
      <Route path="/admin/profile"       element={<ProtectedRoute allowedRoles={['admin']}><ProfilePage        /></ProtectedRoute>} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 3500,
            style: { borderRadius: '12px', fontFamily: 'system-ui', fontSize: '14px' },
            success: { style: { background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534' } },
            error:   { style: { background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b' } },
          }}
        />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
